'use client'

import {
  createContext,
  useState,
} from 'react'
import { usePathname } from 'next/navigation'
import { motion, MotionConfig, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'

import { Header } from '@/components/ui/header'
import { Footer } from '@/components/Footer'
import { GridPattern } from '@/components/GridPattern'
import { Analytics } from "@vercel/analytics/react"

const RootLayoutContext = createContext(null)

function RootLayoutInner({ children }) {
  let shouldReduceMotion = useReducedMotion()
  let pathname = usePathname()
  let isHome = pathname === '/'
  // The application chat is a viewport-locked screen (ChatGPT-style pinned
  // composer) — a footer below it would just force the body to scroll.
  let isChatScreen = pathname.startsWith('/zero-to-hero/apply')

  return (
    <MotionConfig transition={shouldReduceMotion ? { duration: 0 } : undefined}>
      <Header />

      <div
        style={{ borderTopLeftRadius: isHome ? 0 : 40, borderTopRightRadius: isHome ? 0 : 40 }}
        className={cn(
          "relative flex flex-auto",
          // `overflow-clip` (not `overflow-hidden`) on non-home routes: both
          // clip visual overflow identically, but `hidden` also creates a
          // real scroll container — one the browser's native "scroll the
          // focused element into view" behavior (e.g. a growing textarea) or
          // a descendant's scrollIntoView() can silently move via scrollTop,
          // which shifts this entire wrapper and any viewport-locked screen
          // beneath it (the Z2H apply chat) out of alignment with no visible
          // scrollbar to explain why. `clip` cannot be scrolled at all, so
          // it keeps the same rounded-corner clipping with no accidental
          // scroll target.
          isHome ? "overflow-x-clip" : "overflow-clip",
          isHome ? "bg-neutral-50 pt-0" : "bg-white pt-24"
        )}
      >
        <div
          className={cn(
            "relative isolate flex w-full flex-col",
            isHome ? "pt-0" : "pt-9"
          )}
        >
          <GridPattern
            className="absolute inset-x-0 -top-14 -z-10 h-[1000px] w-full fill-neutral-50 stroke-neutral-950/5 [mask-image:linear-gradient(to_bottom_left,white_40%,transparent_50%)]"
            yOffset={-96}
            interactive
          />

          <main className="w-full flex-auto">{children}</main>

          {!isChatScreen && <Footer />}
        </div>
      </div>
      <Analytics />
    </MotionConfig>
  )
}

export function RootLayout({ children }) {
  let pathname = usePathname()
  let [logoHovered, setLogoHovered] = useState(false)

  return (
    <RootLayoutContext.Provider value={{ logoHovered, setLogoHovered }}>
      <RootLayoutInner key={pathname}>{children}</RootLayoutInner>
    </RootLayoutContext.Provider>
  )
}
